import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LoggerService } from '@lbmx/phoenix-lib-core';
import {
  SpinnerService,
  ToastrNotificationService
} from '@lbmx/phoenix-lib-utils';
import { EnabledMode, IButton, IPaging } from '@lbmx/search-container/api';
import { TranslocoService } from '@ngneat/transloco';
import { from, Observable, of, Subscription } from 'rxjs';
import { finalize, map, startWith } from 'rxjs/operators';
import { IAddOnboardingMemberRequest, IConfigCountry, IConfigProvince, IDropDown, IMemberLocationForm } from 'src/app/dtos/onboarding/onboarding.dto';
import { OnboardingService } from '../../../../services/onboarding/onboarding.service';
import { MemberLocationComponent } from './member-location/member-location.component';

@Component({
  selector: 'memberonboarding',
  templateUrl: './member.component.html',
  styleUrls: ['./member.component.scss'],
})

export class MemberComponent implements OnInit, OnDestroy {

  @ViewChild(MemberLocationComponent) public child: MemberLocationComponent;

  public subscriptions: Subscription[] = [];
  public countriesDropDown: IDropDown[];
  public provincesDropDown: IDropDown[];
  public provinces: IConfigProvince[];
  public deliveryLocations: IMemberLocationForm[] = [{
    id: 0,
    legalcompanyName: 'company1',
    operatingName: 'company1',
    address: 'regre',
    city: 'strfewfweing',
    country: 'OD',
    province: 'GUE',
    postalCode: 'hgfhgf',
    mainPhone: '3243242',
  }, {
    id: 1,
    legalcompanyName: 'company2',
    operatingName: 'company2',
    address: 'tre',
    city: 'hgrthrt',
    country: 'OD',
    province: 'GUE',
    postalCode: 'fdgdf',
    mainPhone: '345435',
  }, {
    id: 2,
    legalcompanyName: 'company3',
    operatingName: 'company3',
    address: 'strretreing',
    city: 'trhtr',
    country: 'OD',
    province: 'GUE',
    postalCode: 'gfdgd',
    mainPhone: '123123',
  }, {
    id: 3,
    legalcompanyName: 'company4',
    operatingName: 'company4',
    address: 'tertretre',
    city: 'werwe',
    country: 'OD',
    province: 'GUE',
    postalCode: 'sadsa',
    mainPhone: '1221312',
  }];
  public deliveryLocations$: Observable<IMemberLocationForm[]> = of(this.deliveryLocations);
  public showLocationDialog = false;
  public maxId = 3;

  public buttons$: Observable<IButton[]> = this.translocoService
    .selectTranslate('ACTIONS')
    .pipe(
      startWith([]),
      map((translations) => [
        {
          name: 'new',
          label: translations.NEW,
          mode: EnabledMode.ALWAYS,
          disabled: false,
        },
        {
          name: 'edit',
          label: translations.EDIT,
          mode: EnabledMode.SINGLE,
          disabled: false,
        },
        {
          name: 'delete',
          label: translations.DELETE,
          mode: EnabledMode.MULTIPLE,
          disabled: false,
        },
      ])
    );

  public displayedColumns: string[] = [];
  public columns: any[];
  public paging: IPaging;

  public memberProfileForm: FormGroup = this.formBuilder.group(
    {
      legalcompanyName: ['', [Validators.required]],
      operatingName: ['', [Validators.required]],
      address: ['', [Validators.required]],
      city: ['', [Validators.required]],
      country: ['', [Validators.required]],
      province: ['', [Validators.required]],
      postalCode: ['', [Validators.required]],
      mainPhone: ['', [Validators.required]],
      generalName: ['', [Validators.required]],
      generalPhone: ['', [Validators.required]],
      generalEmail: ['', [Validators.required, Validators.email]],
      accountingName: ['', [Validators.required]],
      accountingPhone: ['', [Validators.required]],
      accountingEmail: ['', [Validators.required, Validators.email]],
      ediName: ['', [Validators.required]],
      ediPhone: ['', [Validators.required]],
      ediEmail: ['', [Validators.required, Validators.email]],
    },
  );

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private loggerService: LoggerService,
    private toastSrv: ToastrNotificationService,
    private spinnerService: SpinnerService,
    private onboardingService: OnboardingService,
    private translocoService: TranslocoService,
  ) { }

  public ngOnInit(): void {
    this.columns = [
      { field: 'legalcompanyName', header: 'Legal Company Name' },
      { field: 'operatingName', header: 'Operating Name' },
      { field: 'address', header: 'Address' },
      { field: 'city', header: 'City' },
      { field: 'country', header: 'Country' },
      { field: 'province', header: 'Province | State' },
      { field: 'postalCode', header: 'Zip | PostalCode' },
      { field: 'mainPhone', header: 'Phone Number' }
    ];
    this.displayedColumns = ['legalcompanyName', 'operatingName', 'address', 'city', 'country', 'province', 'postalCode', 'mainPhone'];
    this.paging = {
      sizes: [25, 50, 75, 100],
      page: { first: 0, rows: 25 },
      records: 250,
    };

    this.getCountries();
    this.getProvinces();
  }

  public getCountries() {
    this.onboardingService.getCountries()
      .pipe()
      .subscribe((val) => {
        this.countriesDropDown = val.map(
          (country: IConfigCountry): IDropDown => {
            return {
              label: country.countryName,
              value: country.countryCode.trim(),
            };
          }
        );
      });
  }

  public getProvinces() {
    this.onboardingService.getProvinces()
      .pipe()
      .subscribe((val) => {
        this.provinces = val;
      });
  }

  public onCountryDropDownSelect() {
    const filteredProv = this.provinces.filter(prov => prov.countryCode === this.memberProfileForm.get('country').value);
    this.provincesDropDown = filteredProv.map((province: IConfigProvince): IDropDown => {
      return {
        label: province.provinceName,
        value: province.provinceCode.trim(),
      };
    });
  }

  public ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }
    this.spinnerService.off();
  }

  public buildAddRequest(): IAddOnboardingMemberRequest {
    return {
      legalcompanyName: this.memberProfileForm.value.legalcompanyName,
      operatingName: this.memberProfileForm.value.operatingName,
      address: this.memberProfileForm.value.address,
      city: this.memberProfileForm.value.city,
      country: this.memberProfileForm.value.country,
      province: this.memberProfileForm.value.province,
      postalCode: this.memberProfileForm.value.postalCode,
      mainPhone: this.memberProfileForm.value.mainPhone,
      generalName: this.memberProfileForm.value.generalName,
      generalPhone: this.memberProfileForm.value.generalPhone,
      generalEmail: this.memberProfileForm.value.generalEmail,
      accountingName: this.memberProfileForm.value.accountingName,
      accountingPhone: this.memberProfileForm.value.accountingPhone,
      accountingEmail: this.memberProfileForm.value.accountingEmail,
      ediName: this.memberProfileForm.value.ediName,
      ediPhone: this.memberProfileForm.value.ediPhone,
      ediEmail: this.memberProfileForm.value.ediEmail,
      bgsMemberLocations: JSON.stringify(this.deliveryLocations),
    };
  }

  public save() {
    this.addMember();
  }

  public addMember() {
    this.spinnerService.on();
    this.subscriptions.push(
      this.onboardingService
        .submitMember(this.buildAddRequest())
        .pipe(finalize(() => this.spinnerService.off()))
        .subscribe(
          (response) => {
            this.loggerService.info(
              'AddMemberResponse',
              'AddMemberService.addMember',
              JSON.stringify(response)
            );
            if (response.wasAdded === true) {
              this.toastSrv.successNotify(
                this.translocoService.translate(
                  'ONBOARDING.ADD_MEMBER_SUCCESS'
                ),
                null,
                5000
              );
              this.router.navigate(['..'], { relativeTo: this.route });
            }
          },
          (error) => {
            if (error.error != null && error.error.errorCode === 3) {
              this.toastSrv.warnNotify(
                this.translocoService.translate(
                  'ONBOARDING.MEMBER_ALREADY_EXIST'
                ),
                null,
                5000
              );
            } else {
              this.toastSrv.errorNotify(
                this.translocoService.translate(
                  'ONBOARDING.ADD_MEMBER_FAILURE'
                ),
                null,
                5000
              );
            }
            this.loggerService.error(
              'AddMemberResponse',
              'AddMemberService.addMember',
              JSON.stringify(error)
            );
          }
        )
    );
  }

  public onToolbarClick({ command, selection, clearSelection }) {
    switch (command) {
      case 'new':
        this.showLocationDialog = true;
        clearSelection();
        break;
      case 'edit':
        this.editLocation(selection, clearSelection);
        break;
      case 'delete':
        this.deleteLocations(selection, clearSelection);
        break;
      default:
        break;
    }
  }

  public editLocation(selectedLocation: IMemberLocationForm[], clearSelection) {
    this.child.prefillForm(selectedLocation[0]);
    clearSelection();
    this.showLocationDialog = true;
  }

  public deleteLocations(selectedLocations: IMemberLocationForm[], clearSelection) {
    from(selectedLocations).subscribe(
      (selectedLocation) => {
        this.deliveryLocations$.subscribe(current => {
          const pos = current.findIndex(location => selectedLocation.id === location.id);
          current.splice(pos, 1);
        });
      }
    );
    clearSelection();
  }

  public closeLocationDialog(deliveryLocation: IMemberLocationForm) {
    this.showLocationDialog = false;
    if (deliveryLocation != null) {
      of(deliveryLocation).subscribe(
        (location) => {
          this.deliveryLocations$.subscribe(current => {
            if (location.id === -1) {
              location.id = ++this.maxId;
              current.push(location);
            } else {
              const pos = current.findIndex(loc => location.id === loc.id);
              if (pos > -1) {
                current[pos] = location;
              }
            }
          });
        }
      );
    }
  }
}
